import React, { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  CustomBackdrop,
  EmptyStateBox,
  IconErrorSmall,
  IconToastError,
  IconWarning,
  SubmitButton,
} from '../../../../../components';
import { GenericInputChannelStateResponse } from '../../../../../data-access/gql-types/graphql';
import { InputConfiguration } from './components/input-configuration';
import { useDeviceBindings } from './hooks/use-device-bindings';
import { useSubmitBindings } from './hooks/use-submit-bindings';
import './index.scss';
import { DeviceBindingsForm } from './types';

const DeviceBindings: React.FC = () => {
  const { t } = useTranslation('device-settings');
  const { loading, inputs, form, channels } = useDeviceBindings();
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const { handleSubmitBindings } = useSubmitBindings();

  const handleSubmit = (values: DeviceBindingsForm) => {
    handleSubmitBindings(values, setErrors);
  };

  return (
    <div className="device-bindings">
      {inputs.length ? (
        <FormProvider {...form}>
          <form className="m-b-48" onSubmit={form.handleSubmit(handleSubmit)}>
            {inputs.map((input, index) => {
              const bindingsErrors = form.formState.errors || [];
              const errorInside = !!bindingsErrors?.bindings?.[index] || !!errors[input.id];
              const inputErrors = errors[input.id] || [];

              return (
                <Accordion
                  key={input.id}
                  kind="custom"
                  title={`${t('input')} ${input.alias}`}
                  errorInside={errorInside}
                  {...(errorInside ? { titleIcon: <IconToastError /> } : {})}
                >
                  <>
                    {inputErrors.length
                      ? inputErrors.map((x) => (
                          <div key={x} className="error-message m-b-16">
                            <IconErrorSmall /> <span>{x}</span>
                          </div>
                        ))
                      : null}
                    <InputConfiguration
                      channels={channels}
                      index={index}
                      areCloudActionsSupported={
                        (input.payload as GenericInputChannelStateResponse).areCloudActionsSupported
                      }
                    />
                  </>
                </Accordion>
              );
            })}
            <SubmitButton />
          </form>
        </FormProvider>
      ) : (
        <>{!loading && <EmptyStateBox content={t('bindings.noGenericInputs')} icon={<IconWarning />} />}</>
      )}
      <CustomBackdrop loading={loading} />
    </div>
  );
};

export default DeviceBindings;
